import { inject, Injectable, type Type } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { type Observable } from 'rxjs';
import { mergeMap, withLatestFrom } from 'rxjs/operators';

import { type RootState } from '../core/core.module';
import { type Action } from '../core/store/interfaces';

@Injectable({ providedIn: 'root' })
export abstract class BaseEffects {
  protected _store$ = inject<Store<RootState>>(Store);
  protected _actions$ = inject<Actions<any | any[]>>(Actions);

  public generateEffect<A extends Action>(
    action: Type<A>,
    cb: (a: A) => Observable<A>,
  ): Observable<A>;

  public generateEffect<A extends Action, B>(
    action: Type<A>,
    o1: Observable<B>,
    cb: (a: A, b: B) => Observable<A>,
  ): Observable<A>;

  public generateEffect<A extends Action, B, C>(
    action: Type<A>,
    o1: Observable<B>,
    o2: Observable<C>,
    cb: (a: A, b: B, c: C) => Observable<A>,
  ): Observable<A>;

  public generateEffect<A extends Action, B, C, D>(
    action: Type<A>,
    o1: Observable<B>,
    o2: Observable<C>,
    o3: Observable<D>,
    cb: (a: A, b: B, c: C, d: D) => Observable<A>,
  ): Observable<A>;

  public generateEffect<A extends Action, B, C, D, E>(
    action: Type<A>,
    o1: Observable<B>,
    o2: Observable<C>,
    o3: Observable<D>,
    o4: Observable<E>,
    cb: (a: A, b: B, c: C, d: D, e: E) => Observable<A>,
  ): Observable<A>;

  public generateEffect<A extends Action, B, C, D, E, F>(
    action: Type<A>,
    o1: Observable<B>,
    o2: Observable<C>,
    o3: Observable<D>,
    o4: Observable<E>,
    o5: Observable<F>,
    cb: (a: A, b: B, c: C, d: D, e: E, f: F) => Observable<A>,
  ): Observable<A>;

  public generateEffect<A extends Action, B, C, D, E, F, G>(
    action: Type<A>,
    o1: Observable<B>,
    o2: Observable<C>,
    o3: Observable<D>,
    o4: Observable<E>,
    o5: Observable<F>,
    o6: Observable<G>,
    cb: (a: A, b: B, c: C, d: D, e: E, f: F, g: G) => Observable<A>,
  ): Observable<A>;

  public generateEffect<A extends Action, B, C, D, E, F, G, H>(
    action: Type<A>,
    o1: Observable<B>,
    o2: Observable<C>,
    o3: Observable<D>,
    o4: Observable<E>,
    o5: Observable<F>,
    o6: Observable<G>,
    o7: Observable<H>,
    cb: (a: A, b: B, c: C, d: D, e: E, f: F, g: G, h: H) => Observable<A>,
  ): Observable<A>;

  public generateEffect<A extends Action, B, C, D, E, F, G, H, I>(
    action: Type<A>,
    o1: Observable<B>,
    o2: Observable<C>,
    o3: Observable<D>,
    o4: Observable<E>,
    o5: Observable<F>,
    o6: Observable<G>,
    o7: Observable<H>,
    o8: Observable<I>,
    cb: (a: A, b: B, c: C, d: D, e: E, f: F, g: G, h: H, i: I) => Observable<A>,
  ): Observable<A>;

  public generateEffect<A extends Action, B, C, D, E, F, G, H, I, J>(
    action: Type<A>,
    o1: Observable<B>,
    o2: Observable<C>,
    o3: Observable<D>,
    o4: Observable<E>,
    o5: Observable<F>,
    o6: Observable<G>,
    o7: Observable<H>,
    o8: Observable<I>,
    o9: Observable<J>,
    cb: (
      a: A,
      b: B,
      c: C,
      d: D,
      e: E,
      f: F,
      g: G,
      h: H,
      i: I,
      j: J,
    ) => Observable<A>,
  ): Observable<A>;

  public generateEffect<A extends Action, B, C, D, E, F, G, H, I, J, K>(
    action: Type<A>,
    o1: Observable<B>,
    o2: Observable<C>,
    o3: Observable<D>,
    o4: Observable<E>,
    o5: Observable<F>,
    o6: Observable<G>,
    o7: Observable<H>,
    o8: Observable<I>,
    o9: Observable<J>,
    o10: Observable<K>,
    cb: (
      a: A,
      b: B,
      c: C,
      d: D,
      e: E,
      f: F,
      g: G,
      h: H,
      i: I,
      j: J,
      k: K,
    ) => Observable<A>,
  ): Observable<A>;

  public generateEffect<A extends Action>(
    action: Type<A>,
    ...args: any[]
  ): Observable<Action | Action[]> {
    const cb: any = args.pop();

    return this._actions$.pipe(
      ofType((action as any).type),
      withLatestFrom(...(args as Observable<any>[])),
      mergeMap(r => (Array.isArray(r) ? cb(...r) : cb(r))),
    ) as any;
  }
}
